/**
 * Section render registry for PMB v2.
 *
 * Maps section_id → React render function. The widget's main loop calls
 * `renderSection(id, output)` for each section in `section_order`.
 *
 * Adding a new section is one entry here + one prompt + one data fetcher
 * on the py side. Default renderer (markdown) covers most narrative
 * sections; structured ones (buying_committee, tech_stack,
 * content_recommender) get bespoke renderers.
 */
import {
  Briefcase,
  Building2,
  Cpu,
  Crosshair,
  Lightbulb,
  type LucideIcon,
  Mail,
  MapPin,
  Newspaper,
  Sparkles,
  Swords,
  Target,
  TrendingUp,
  User,
  Users
} from "lucide-react"
import type { JSX } from "react"

import MarkdownRender from "~/components/markdown-render"
import type { PmbV2SectionOutput } from "~/store/pmb-v2"

export const PMB_V2_THIRTY_SEC_ID = "30_sec_brief"

export type PmbV2Tab = "person" | "account"

/** Stable rep-facing labels per section_id. Keep in sync with py registry titles. */
export const PMB_V2_SECTION_TITLES: Record<string, string> = {
  "30_sec_brief": "30-Second Brief",
  person_summary: "Person Summary",
  account_summary: "Account Summary",
  account_priorities: "Account Priorities",
  news: "Recent News",
  buying_committee: "Buying Committee",
  coaching_pack: "Coaching Pack",
  tech_stack: "Tech Stack",
  industry_trends: "Industry Trends",
  competitors: "Competitors",
  talking_points: "Talking Points",
  strategic_agenda: "Strategic Agenda",
  content_recommender: "Content to Send"
}

/**
 * Tab + sub-section assignment per section_id.
 *
 * Mirrors v1's structure (PERSON BRIEF / ACCOUNT BRIEF) so reps can flip
 * between two related views and use the inline pill nav for sub-sections.
 * 30_sec_brief renders ABOVE both tabs (accent card) regardless of mapping.
 */
export const PMB_V2_SECTION_TABS: Record<
  string,
  { tab: PmbV2Tab; subsection: string }
> = {
  "30_sec_brief": { tab: "person", subsection: "30_sec_brief" }, // pinned above tabs
  person_summary: { tab: "person", subsection: "about_person" },
  talking_points: { tab: "person", subsection: "talking_points" },
  coaching_pack: { tab: "person", subsection: "talking_points" },
  account_summary: { tab: "account", subsection: "about_account" },
  strategic_agenda: { tab: "account", subsection: "strategic_agenda" },
  account_priorities: { tab: "account", subsection: "strategic_agenda" },
  buying_committee: { tab: "account", subsection: "strategic_agenda" },
  news: { tab: "account", subsection: "about_account" },
  competitors: { tab: "account", subsection: "about_account" },
  industry_trends: { tab: "account", subsection: "about_account" },
  tech_stack: { tab: "account", subsection: "about_account" },
  content_recommender: { tab: "account", subsection: "about_account" }
}

/** Sub-section pill labels per tab — ordered. Icons match v1 nav style. */
export const PMB_V2_SUBSECTION_LABELS: Record<
  PmbV2Tab,
  Array<{ key: string; label: string; icon: LucideIcon }>
> = {
  person: [
    { key: "about_person", label: "About", icon: User },
    { key: "talking_points", label: "Talking points", icon: Lightbulb }
  ],
  account: [
    { key: "about_account", label: "About", icon: Building2 },
    { key: "strategic_agenda", label: "Priorities", icon: Target }
  ]
}

/**
 * Per-section visual meta — icon, subtitle, color tone. The section
 * header in the widget renders this as: [icon-badge] Title / subtitle.
 * Matches v1's pattern from the screenshots:
 *   • About <Name>          → User icon, "Background, experience…"
 *   • Talking points        → Lightbulb, "Key conversation starters…"
 *   • Key Priorities for …  → Target, "Account priorities this person…"
 *   • Know your prospect    → Crosshair, "Personality insights…"
 */
export const PMB_V2_SECTION_META: Record<
  string,
  {
    icon: LucideIcon
    subtitle?: string
    tone:
      | "slate"
      | "indigo"
      | "blue"
      | "emerald"
      | "amber"
      | "rose"
      | "cyan"
      | "teal"
      | "purple"
  }
> = {
  "30_sec_brief": {
    icon: Sparkles,
    subtitle: "Executive summary for the next 30 seconds",
    tone: "indigo"
  },
  person_summary: {
    icon: User,
    subtitle: "Background, experience, and expertise",
    tone: "slate"
  },
  talking_points: {
    icon: Lightbulb,
    subtitle: "Key conversation starters for your meeting",
    tone: "amber"
  },
  coaching_pack: {
    icon: Crosshair,
    subtitle: "Likely objections, tough questions, and how to handle them",
    tone: "rose"
  },
  account_summary: {
    icon: Building2,
    subtitle: "Company snapshot, business, and footprint",
    tone: "blue"
  },
  account_priorities: {
    icon: Target,
    subtitle: "Account priorities this person is associated with",
    tone: "blue"
  },
  strategic_agenda: {
    icon: TrendingUp,
    subtitle: "Strategic themes shaping the account right now",
    tone: "emerald"
  },
  buying_committee: {
    icon: Users,
    subtitle: "Key stakeholders likely to influence the deal",
    tone: "teal"
  },
  news: {
    icon: Newspaper,
    subtitle: "Recent signals from the account and the person",
    tone: "cyan"
  },
  competitors: {
    icon: Swords,
    subtitle: "Likely competitors and how to differentiate",
    tone: "rose"
  },
  industry_trends: {
    icon: TrendingUp,
    subtitle: "Sector trends framing this conversation",
    tone: "emerald"
  },
  tech_stack: {
    icon: Cpu,
    subtitle: "Technologies in use at this account",
    tone: "cyan"
  },
  content_recommender: {
    icon: Mail,
    subtitle: "Curated tenant collateral to share post-meeting",
    tone: "blue"
  }
}

/** Tailwind class bundles per tone — used by SectionCard's icon badge. */
export const PMB_V2_TONE_CLASSES: Record<
  | "slate"
  | "indigo"
  | "blue"
  | "emerald"
  | "amber"
  | "rose"
  | "cyan"
  | "teal"
  | "purple",
  { badgeBg: string; badgeFg: string; accentBorder: string }
> = {
  slate: {
    badgeBg: "bg-slate-100 dark:bg-slate-800",
    badgeFg: "text-slate-700 dark:text-slate-200",
    accentBorder: "border-l-slate-500"
  },
  indigo: {
    badgeBg: "bg-indigo-100 dark:bg-indigo-900/40",
    badgeFg: "text-indigo-700 dark:text-indigo-200",
    accentBorder: "border-l-indigo-500"
  },
  blue: {
    badgeBg: "bg-blue-100 dark:bg-blue-900/40",
    badgeFg: "text-blue-700 dark:text-blue-200",
    accentBorder: "border-l-blue-500"
  },
  emerald: {
    badgeBg: "bg-emerald-100 dark:bg-emerald-900/40",
    badgeFg: "text-emerald-700 dark:text-emerald-200",
    accentBorder: "border-l-emerald-500"
  },
  amber: {
    badgeBg: "bg-amber-100 dark:bg-amber-900/40",
    badgeFg: "text-amber-700 dark:text-amber-200",
    accentBorder: "border-l-amber-500"
  },
  rose: {
    badgeBg: "bg-rose-100 dark:bg-rose-900/40",
    badgeFg: "text-rose-700 dark:text-rose-200",
    accentBorder: "border-l-rose-500"
  },
  cyan: {
    badgeBg: "bg-cyan-100 dark:bg-cyan-900/40",
    badgeFg: "text-cyan-700 dark:text-cyan-200",
    accentBorder: "border-l-cyan-500"
  },
  teal: {
    badgeBg: "bg-teal-100 dark:bg-teal-900/40",
    badgeFg: "text-teal-700 dark:text-teal-200",
    accentBorder: "border-l-teal-500"
  },
  purple: {
    badgeBg: "bg-purple-100 dark:bg-purple-900/40",
    badgeFg: "text-purple-700 dark:text-purple-200",
    accentBorder: "border-l-purple-500"
  }
}

type SectionRenderer = (output: PmbV2SectionOutput) => JSX.Element | null

/**
 * Strip trailing period/colon from inline bold sub-section labels.
 *
 * The LLM has historically emitted prompt-style `**Snapshot.** body`
 * paragraphs (the prompts told it to). Updated prompts now ask for
 * `**Snapshot**\n\nbody`, but cached outputs and the occasional drift
 * still produce the period. Cleaning at the renderer keeps the visual
 * consistent regardless of upstream.
 *
 * Targets ONLY bold labels at paragraph starts whose content looks like
 * a label (≤60 chars, no internal periods) — won't touch in-sentence
 * bold like "**Acuity Brands.** is a leader…".
 */
function _cleanInlineBoldLabels(content: string): string {
  if (!content) return content
  return content.replace(
    /(^|\n)\*\*([^*\n.:]{1,60})[.:]\*\*/g,
    (_m, lead, label) => `${lead}**${label.trimEnd()}**`
  )
}

const renderMarkdown: SectionRenderer = (output) => {
  if (!output?.content) return null
  return <MarkdownRender content={_cleanInlineBoldLabels(output.content)} />
}

/**
 * Talking-points card grid — parses an ordered list out of the LLM
 * markdown and renders each item as a numbered card with rotating
 * accent color (matches v1's purple/blue/green/orange palette in the
 * screenshot).
 *
 * Accepts either:
 *   - payload.points: [{title, body}] — preferred structured shape
 *   - content: markdown with "1. **Title**\nbody" sections — fallback
 */
/**
 * Per-card tone palette for talking-points. v1's screenshot uses a 4-color
 * cycle (purple, blue, emerald, orange) with each card's LEFT BORDER
 * matching the numbered badge. We extend to 6 so longer talking-point
 * lists don't repeat too quickly.
 */
const _TALKING_TONES: Array<{
  badge: string
  border: string
}> = [
  { badge: "bg-purple-500", border: "border-l-purple-500" },
  { badge: "bg-blue-500", border: "border-l-blue-500" },
  { badge: "bg-emerald-500", border: "border-l-emerald-500" },
  { badge: "bg-orange-500", border: "border-l-orange-500" },
  { badge: "bg-pink-500", border: "border-l-pink-500" },
  { badge: "bg-amber-500", border: "border-l-amber-500" }
]

function _parseTalkingPoints(
  content: string
): Array<{ title: string; body: string }> {
  if (!content) return []
  const lines = content.split("\n")
  const points: Array<{ title: string; body: string }> = []
  let current: { title: string; body: string } | null = null
  const numberedRe = /^\s*(\d+)\.\s+(.+)$/
  const headerRe = /\*\*(.+?)\*\*\s*:?\s*(.*)$/

  for (const raw of lines) {
    const line = raw.trim()
    const m = line.match(numberedRe)
    if (m) {
      if (current) points.push(current)
      const rest = m[2]
      const hm = rest.match(headerRe)
      current = hm
        ? { title: hm[1].trim(), body: (hm[2] || "").trim() }
        : { title: rest.trim(), body: "" }
    } else if (current && line) {
      current.body = current.body ? `${current.body} ${line}`.trim() : line
    }
  }
  if (current) points.push(current)
  return points
}

const renderTalkingPoints: SectionRenderer = (output) => {
  const payloadPoints =
    (output?.payload?.points as Array<{ title?: string; body?: string }>) ??
    null
  let points: Array<{ title: string; body: string }> = []
  if (Array.isArray(payloadPoints) && payloadPoints.length) {
    points = payloadPoints.map((p) => ({
      title: p.title || "",
      body: p.body || ""
    }))
  } else if (output?.content) {
    points = _parseTalkingPoints(output.content)
  }

  if (!points.length) {
    return output?.content ? (
      <MarkdownRender content={_cleanInlineBoldLabels(output.content)} />
    ) : null
  }

  return (
    <div className="space-y-3">
      {points.map((p, i) => {
        const tone = _TALKING_TONES[i % _TALKING_TONES.length]
        return (
          <div
            key={`${p.title}-${i}`}
            className={`flex gap-3 rounded-md border border-gray-200 dark:border-gray-700 border-l-4 ${tone.border} bg-gray-50/50 dark:bg-gray-900/40 px-4 py-3`}
          >
            <div
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${tone.badge} text-xs font-bold text-white`}
            >
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              {p.title ? (
                <h4 className="text-sm font-semibold mb-1 text-gray-900 dark:text-gray-50">
                  {p.title}
                </h4>
              ) : null}
              {p.body ? (
                <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                  {p.body}
                </p>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function _initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("")
}

/**
 * Person Summary — v1 layout:
 *   [Initials avatar (purple)] [LinkedIn icon] Name (big bold)
 *   💼 Title  ·  📍 Location                       [Get email]  [Generate message ▾]
 *   ───────────────────────────────────────────────
 *   Body markdown
 */
const renderPersonSummary: SectionRenderer = (output) => {
  const person =
    (output?.payload?.person as {
      fullname?: string
      title?: string
      headline?: string
      addresscountryonly?: string
      location?: string
      linkedin_url?: string
    }) ?? {}
  const name = person.fullname || ""
  const subLine = person.title || person.headline || ""
  const loc = person.addresscountryonly || person.location || ""
  const linkedin = person.linkedin_url || ""

  return (
    <div className="space-y-4">
      {name || subLine || loc ? (
        <div className="flex items-start gap-3">
          {name ? (
            <div
              aria-hidden
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-slate-600 to-slate-800 text-white text-sm font-semibold ring-1 ring-slate-300 dark:ring-slate-600"
            >
              {_initials(name)}
            </div>
          ) : null}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {linkedin ? (
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open LinkedIn profile"
                  title="Open LinkedIn profile"
                  className="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-[#0a66c2] text-[11px] font-bold text-white hover:opacity-90"
                >
                  in
                </a>
              ) : null}
              {name ? (
                <span className="text-xl font-bold text-gray-900 dark:text-gray-50">
                  {name}
                </span>
              ) : null}
            </div>
            {subLine || loc ? (
              <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-600 dark:text-gray-300">
                {subLine ? (
                  <span className="inline-flex items-center gap-1">
                    <Briefcase className="h-3 w-3" aria-hidden />
                    {subLine}
                  </span>
                ) : null}
                {subLine && loc ? (
                  <span aria-hidden className="text-gray-400">
                    ·
                  </span>
                ) : null}
                {loc ? (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" aria-hidden />
                    {loc}
                  </span>
                ) : null}
              </div>
            ) : null}
            {linkedin ? (
              <div className="mt-2">
                <a
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <span
                    aria-hidden
                    className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm bg-[#0a66c2] text-[9px] font-bold text-white"
                  >
                    in
                  </span>
                  Open LinkedIn
                </a>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {output?.content ? (
        <MarkdownRender content={_cleanInlineBoldLabels(output.content)} />
      ) : null}
    </div>
  )
}

/**
 * Account Summary — v1 layout:
 *   [Initial-square (blue)] Name (big bold)
 *   [🌐 Visit website] [in LinkedIn] [WEB-DERIVED?]
 *   ───────────────────────────────────────────────
 *   Body markdown
 */
const renderAccountSummary: SectionRenderer = (output) => {
  const acct =
    (output?.payload?.account as {
      name?: string
      website?: string
      linkedin_link?: string
      tier?: number
    }) ?? {}
  const cxos =
    (output?.payload?.cxos as
      | Array<{ name?: string; title?: string; linkedin_url?: string }>
      | undefined) ?? []
  const isTier2 = acct.tier === 2
  const name = acct.name || ""
  const website = acct.website || ""
  const websiteHref = website
    ? website.startsWith("http")
      ? website
      : `https://${website}`
    : ""
  const linkedin = acct.linkedin_link || ""

  return (
    <div className="space-y-4">
      {name ? (
        <div className="flex items-start gap-3">
          <div
            aria-hidden
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white text-base font-bold"
          >
            {name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xl font-bold text-gray-900 dark:text-gray-50">
                {name}
              </span>
              {isTier2 ? (
                <span className="rounded-full bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold text-amber-800 dark:text-amber-200">
                  web-derived
                </span>
              ) : null}
            </div>
            {websiteHref || linkedin ? (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {websiteHref ? (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <span aria-hidden>🌐</span>
                    Visit website
                  </a>
                ) : null}
                {linkedin ? (
                  <a
                    href={linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <span
                      aria-hidden
                      className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm bg-[#0a66c2] text-[9px] font-bold text-white"
                    >
                      in
                    </span>
                    LinkedIn
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {cxos.length ? (
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
            Key executives
          </h4>
          <ul className="space-y-1">
            {cxos.map((p, i) => (
              <li
                key={`${p.name}-${i}`}
                className="text-sm flex items-center gap-1.5 flex-wrap"
              >
                {p.linkedin_url ? (
                  <a
                    href={p.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open LinkedIn profile for ${p.name || "executive"}`}
                    title="Open LinkedIn profile"
                    className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-[#0a66c2] text-[9px] font-bold text-white hover:opacity-90 shrink-0"
                  >
                    in
                  </a>
                ) : null}
                <span className="font-medium">{p.name}</span>
                {p.title ? (
                  <span className="text-gray-500"> — {p.title}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {output?.content ? (
        <MarkdownRender content={_cleanInlineBoldLabels(output.content)} />
      ) : null}
    </div>
  )
}

const renderBuyingCommittee: SectionRenderer = (output) => {
  const committee =
    (output?.payload?.committee as
      | Array<{ name?: string; title?: string; linkedin_url?: string }>
      | undefined) ?? []
  if (!committee.length && !output?.content) {
    return (
      <p className="text-xs text-gray-500">
        No buying committee data available.
      </p>
    )
  }
  return (
    <div className="space-y-2">
      {committee.length ? (
        <ul className="space-y-1">
          {committee.map((p, i) => (
            <li
              key={`${p.name}-${i}`}
              className="text-sm flex items-center gap-1.5 flex-wrap"
            >
              {p.linkedin_url ? (
                <a
                  href={p.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open LinkedIn profile for ${p.name || "committee member"}`}
                  title="Open LinkedIn profile"
                  className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-[#0a66c2] text-[9px] font-bold text-white hover:opacity-90 shrink-0"
                >
                  in
                </a>
              ) : null}
              <span className="font-medium">{p.name}</span>
              {p.title ? (
                <span className="text-gray-500"> — {p.title}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {output?.content ? (
        <MarkdownRender content={_cleanInlineBoldLabels(output.content)} />
      ) : null}
    </div>
  )
}

const renderTechStack: SectionRenderer = (output) => {
  const tech = (output?.payload?.tech_stack as string[] | undefined) ?? []
  if (!tech.length) {
    return (
      <p className="text-xs text-gray-500">
        No tech stack data captured for this account yet.
      </p>
    )
  }
  return (
    <div className="flex flex-wrap gap-1">
      {tech.map((t) => (
        <span
          key={t}
          className="text-xs rounded bg-gray-100 dark:bg-gray-800 px-2 py-1"
        >
          {t}
        </span>
      ))}
    </div>
  )
}

const renderContentRecommender: SectionRenderer = (output) => {
  const recs =
    (output?.payload?.recommendations as Array<{
      title?: string
      url?: string
    }>) ?? []
  if (!recs.length) {
    return (
      <p className="text-xs text-gray-500">
        No matched collateral yet. Try a broader Q1 / focus mix.
      </p>
    )
  }
  return (
    <ul className="space-y-1">
      {recs.map((r, i) => (
        <li key={i} className="text-sm">
          {r.url ? (
            <a
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {r.title || r.url}
            </a>
          ) : (
            r.title
          )}
        </li>
      ))}
    </ul>
  )
}

const renderStrategicAgenda: SectionRenderer = (output) => {
  // v1-style 3-up card grid when the py shaper produced `cards`.
  // Each card is {title, description}. Falls back to markdown when
  // no card payload (older account_research blob without strategic_agenda
  // structure or LLM-written summary).
  const cards =
    (output?.payload?.cards as Array<{
      title?: string
      description?: string
    }>) ?? []
  const summary =
    (output?.payload?.summary as string | undefined) || output?.content || ""

  return (
    <div className="space-y-3">
      {summary ? (
        <MarkdownRender content={_cleanInlineBoldLabels(summary)} />
      ) : null}
      {cards.length ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {cards.map((c, i) => (
            <div
              key={`${c.title}-${i}`}
              className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2"
            >
              <h4 className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">
                {c.title || `Theme ${i + 1}`}
              </h4>
              {c.description ? (
                <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-200">
                  {c.description}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const renderCompetitors: SectionRenderer = (output) => {
  const competitors =
    (output?.payload?.competitors as Array<{
      name?: string
      workscope?: string
    }>) ?? []
  if (!competitors.length && !output?.content) {
    return (
      <p className="text-xs text-gray-500">
        No curated competitor list available.
      </p>
    )
  }
  return (
    <div className="space-y-2">
      {competitors.length ? (
        <ul className="space-y-1.5">
          {competitors.map((c, i) => (
            <li key={`${c.name}-${i}`} className="text-sm">
              <span className="font-medium">{c.name}</span>
              {c.workscope ? (
                <span className="text-gray-500"> — {c.workscope}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {output?.content ? (
        <MarkdownRender content={_cleanInlineBoldLabels(output.content)} />
      ) : null}
    </div>
  )
}

const renderNews: SectionRenderer = (output) => {
  const items =
    (output?.payload?.news as Array<{
      title?: string
      summary?: string
      source?: string
      date?: string
      link?: string
      kind?: "person" | "account"
    }>) ?? []
  if (!items.length) {
    return output?.content ? (
      <MarkdownRender content={_cleanInlineBoldLabels(output.content)} />
    ) : (
      <p className="text-xs text-gray-500">No news items.</p>
    )
  }
  return (
    <ul className="space-y-2">
      {items.map((n, i) => (
        <li key={i} className="text-sm">
          {n.kind === "person" ? (
            <span className="mr-1 inline-block rounded bg-blue-100 dark:bg-blue-900 px-1.5 py-0.5 text-[10px] text-blue-700 dark:text-blue-200 uppercase tracking-wide">
              person
            </span>
          ) : null}
          {n.link ? (
            <a
              href={n.link}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
            >
              {n.title}
            </a>
          ) : (
            <span className="font-medium">{n.title}</span>
          )}
          <span className="text-gray-500 text-xs">
            {" "}
            ({n.source || "—"} · {n.date || "—"})
          </span>
          {n.summary ? (
            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300 leading-snug">
              {n.summary}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

const RENDERERS: Record<string, SectionRenderer> = {
  buying_committee: renderBuyingCommittee,
  tech_stack: renderTechStack,
  content_recommender: renderContentRecommender,
  strategic_agenda: renderStrategicAgenda,
  competitors: renderCompetitors,
  news: renderNews,
  talking_points: renderTalkingPoints,
  coaching_pack: renderTalkingPoints, // same numbered-card visual treatment
  person_summary: renderPersonSummary,
  account_summary: renderAccountSummary
}

export function renderSection(
  sectionId: string,
  output: PmbV2SectionOutput
): JSX.Element | null {
  const renderer = RENDERERS[sectionId] ?? renderMarkdown
  return renderer(output)
}

/**
 * Detect whether a section's output is "no signal" / empty so the
 * widget can hide the card entirely. v1 doesn't show empty cards —
 * matches that behavior.
 *
 * Two layers:
 *   1. STRUCTURED PAYLOAD CHECK (preferred, deterministic). For sections
 *      whose value is a curated list (competitors, news, content_recommender,
 *      strategic_agenda, tech_stack, buying_committee, coaching_pack),
 *      "empty payload" alone is enough — the LLM-written narrative is
 *      worthless without the underlying data, even if it has structure
 *      like "### Likely incumbents / alternatives".
 *   2. NARRATIVE FALLBACK for sections without a payload contract
 *      (person_summary, account_summary, talking_points, industry_trends,
 *      30_sec_brief). Hide only when the content reads as a generic
 *      "no signal" stub end-to-end.
 *
 * Sections still in-flight (no content + no payload + no error)
 * return false so the skeleton shows while streaming.
 */
const _EMPTY_PHRASE_RE =
  /(no |not enough|insufficient|none\b|n\/?a\b|no material|no recent|no curated|nothing to|unable to)/i

/** Returns true when the content is a generic no-signal stub.
 *  Strips markdown headings/bullets and checks whether the residual
 *  body is dominated by "no signal" sentences. */
function _isEmptyStub(content?: string | null): boolean {
  if (!content) return false
  const s = content.trim()
  if (!s) return true
  // Strip leading bullet/strong-label so "- No incumbent signal." matches.
  const leadStripped = s
    .replace(/^[-*•]\s*/, "")
    .replace(/^\*\*[^*]+\*\*\s*[.:]?\s*/, "")
  if (_EMPTY_PHRASE_RE.test(leadStripped) && leadStripped.length < 240) {
    return true
  }
  // Body-wide check: strip markdown headings, bullets, bold labels, then
  // count meaningful sentences vs no-signal sentences. When most of the
  // body is "no signal" the whole section is a placeholder.
  const stripped = s
    .replace(/^#+\s.*$/gm, "")
    .replace(/^[-*•]\s*/gm, "")
    .replace(/\*\*[^*\n]+\*\*\s*[.:]?\s*/g, "")
    .trim()
  if (!stripped) return true
  const sentences = stripped
    .split(/(?<=[.!?])\s+|\n+/)
    .map((x) => x.trim())
    .filter((x) => x.length > 4)
  if (sentences.length === 0) return true
  const noSignal = sentences.filter((x) => _EMPTY_PHRASE_RE.test(x)).length
  // ≥60% no-signal sentences AND body under 400 chars → treat as empty.
  return noSignal / sentences.length >= 0.6 && stripped.length < 400
}

export function isSectionEmpty(
  sectionId: string,
  output: PmbV2SectionOutput | undefined
): boolean {
  if (!output) return false // still loading, don't hide
  if (output.error) return false // surface errors, don't hide
  const payload = (output.payload ?? {}) as Record<string, unknown>
  const content = output.content || ""

  switch (sectionId) {
    // ── Structured-payload sections — payload IS the source of truth. ──
    case "competitors": {
      // The shaper sets payload.competitors from research.competitors.
      // When empty, the LLM-written content (industry-only "Likely
      // incumbents… No signal / Where we win… Not enough signal") is
      // never useful — hide unconditionally.
      const competitors = (payload.competitors as unknown[]) ?? []
      return competitors.length === 0
    }
    case "news": {
      const news = (payload.news as unknown[]) ?? []
      return news.length === 0
    }
    case "content_recommender": {
      const recs = (payload.recommendations as unknown[]) ?? []
      return recs.length === 0
    }
    case "strategic_agenda": {
      const cards = (payload.cards as unknown[]) ?? []
      const summary = (payload.summary as string) || ""
      if (cards.length > 0) return false
      // No cards — only show if there's a substantive summary blob.
      return summary.trim().length < 80 && _isEmptyStub(content)
    }
    case "tech_stack": {
      const tech = payload.tech_stack as unknown
      return (
        !tech ||
        (Array.isArray(tech) && tech.length === 0) ||
        (typeof tech === "object" &&
          tech !== null &&
          Object.keys(tech as object).length === 0)
      )
    }
    case "buying_committee": {
      const committee = (payload.committee as unknown[]) ?? []
      return committee.length === 0
    }
    case "coaching_pack": {
      const tips = (payload.tips as unknown[]) ?? []
      if (tips.length > 0) return false
      return _isEmptyStub(content)
    }

    // ── Narrative sections — fall back to content-stub heuristics. ──
    case "industry_trends": {
      const industries = (payload.industries as unknown[]) ?? []
      if (industries.length > 0 || (content || "").trim().length > 80)
        return false
      return _isEmptyStub(content)
    }
    default:
      // person_summary, account_summary, talking_points, 30_sec_brief.
      // These typically have meaningful content even on Tier-2 — hide
      // only when content is an explicit "no signal" stub.
      return _isEmptyStub(content)
  }
}
